/**
 * Pure framing geometry. No DOM, no MediaPipe, no React — this is the only
 * place the framing rules live, and the only part of the webcam feature that
 * is unit-tested.
 *
 * Coordinate conventions:
 *  - MediaPipe keypoints arrive normalised (0..1), bounding boxes in pixels.
 *  - Internals work in pixels, which keeps "square" actually square.
 *  - NormalisedRect goes back out normalised: x/size by frame WIDTH, y by
 *    frame HEIGHT, describing a square in pixel space.
 */

export interface NormalisedPoint {
  x: number;
  y: number;
}

export interface PixelBox {
  originX: number;
  originY: number;
  width: number;
  height: number;
}

export interface RawDetection {
  boundingBox?: PixelBox;
  keypoints: NormalisedPoint[];
}

export interface NormalisedRect {
  x: number;
  y: number;
  size: number;
}

export type FramingHint =
  'noFace' | 'multipleFaces' | 'tooClose' | 'tooFar' | 'offCentre';

export interface Framing {
  ok: boolean;
  hint: FramingHint | null;
  crop: NormalisedRect | null;
}

// ponytail: calibration knobs — a wide-angle laptop camera and a longer lens do
// not frame the same subject the same way. Set these from a real card render,
// not from theory. Tuned for head and shoulders: the head occupies a bit over
// a third of the square, which leaves room for the shoulders below the chin
// and a little air above the skull. Lower HEAD_WIDTH_RATIO for a wider shot,
// and move EYE_LINE_FROM_TOP with it or the extra room lands above the head
// instead of below the chin.
//
// HEAD_WIDTH_RATIO is measured tragion to tragion — ear to ear — while a
// reader judges the head they can see, hair and headphones included, which is
// roughly 1.6× that. In units of the measured width T, with the eye line at
// 0.31 of the side: at 0.38 the square is 2.63 T, leaving 0.10 T above the
// crown and putting the perceived head across 61% of the width — the crown
// grazes the top edge and the shoulders are cut. At 0.30 it is 3.33 T: 0.30 T
// of air above, the perceived head across 48%, and 1.43 T below the chin.
//
// The frame is 4:3, so a square can be at most three quarters of its width:
// at 0.30 a head wider than 0.225 of the frame cannot be framed at all and is
// reported tooClose. The usable band is MIN_HEAD_WIDTH..0.225 — a subject at
// arm's length passes, which the original 0.15 floor refused (it asked a
// correctly framed bust to come closer).
export const FRAMING = {
  /** Head width as a fraction of the square's side. */
  HEAD_WIDTH_RATIO: 0.3,
  /** Eye line height as a fraction of the square's side, from its top. */
  EYE_LINE_FROM_TOP: 0.31,
  /** Below this head width (fraction of frame width) the subject is too far. */
  MIN_HEAD_WIDTH: 0.11,
  /** Clamping the square back in-frame by more than this fraction of its side
   *  means the subject is not centred enough. */
  MAX_CLAMP_LOSS: 0.02,
  /**
   * Schmitt trigger: once a verdict is `ok`, the tooFar and offCentre limits
   * are this much slacker before it is lost again. A subject sitting exactly
   * on a threshold otherwise flips the box green/magenta — and the shutter
   * enabled/disabled — as fast as the detector runs.
   * ponytail: one factor for both limits; split it if only one flickers.
   */
  HYSTERESIS: 1.6,
  /**
   * Below this move (fraction of the square's side) the square does not move
   * at all. The head width is measured tragion to tragion and divided by
   * HEAD_WIDTH_RATIO, so a small wobble on the noisiest keypoints of the model
   * becomes a larger one on the side of the square. Easing alone only divides
   * that; a deadband ends it.
   * ponytail: raise it if the box breathes at rest, lower it if it lags a
   * slow lean.
   */
  DEADBAND: 0.02,
  /** Share of the previous frame kept when easing the square to a new one. */
  SMOOTHING: 0.85,
  /** A jump larger than this fraction of the square's side is a real move (or
   *  another subject): snap, or the box crawls after them. */
  SNAP_DISTANCE: 0.2,
};

/**
 * BlazeFace keypoint order. "Right" is image-relative, i.e. the viewer's left.
 * Read by index behind these names so a model swap has one place to fix.
 */
const KEYPOINT = {
  EYE_RIGHT: 0,
  EYE_LEFT: 1,
  NOSE: 2,
  MOUTH: 3,
  TRAGION_RIGHT: 4,
  TRAGION_LEFT: 5,
} as const;

interface Anchors {
  headWidthPx: number;
  eyeCentreXPx: number;
  eyeLineYPx: number;
}

/**
 * Keypoints give a better head width and a real eye line. When they are absent
 * (fewer than 6 returned) the bounding box still yields a usable, if coarser,
 * frame — detection degrading must not mean no crop at all.
 */
function anchorsOf(
  detection: RawDetection,
  frameW: number,
  frameH: number,
): Anchors | null {
  const kp = detection.keypoints;
  const tragionR = kp[KEYPOINT.TRAGION_RIGHT];
  const tragionL = kp[KEYPOINT.TRAGION_LEFT];
  const eyeR = kp[KEYPOINT.EYE_RIGHT];
  const eyeL = kp[KEYPOINT.EYE_LEFT];
  if (tragionR && tragionL && eyeR && eyeL) {
    return {
      headWidthPx: Math.abs(tragionL.x - tragionR.x) * frameW,
      eyeCentreXPx: ((eyeR.x + eyeL.x) / 2) * frameW,
      eyeLineYPx: ((eyeR.y + eyeL.y) / 2) * frameH,
    };
  }
  const box = detection.boundingBox;
  if (!box) {
    return null;
  }
  return {
    headWidthPx: box.width,
    eyeCentreXPx: box.originX + box.width / 2,
    // Eyes sit around 40% down a face box; coarse, but it beats the centre.
    eyeLineYPx: box.originY + box.height * 0.4,
  };
}

function headWidthOf(detection: RawDetection, frameW: number): number {
  const tragionL = detection.keypoints[KEYPOINT.TRAGION_LEFT];
  const tragionR = detection.keypoints[KEYPOINT.TRAGION_RIGHT];
  if (tragionL && tragionR) {
    return Math.abs(tragionL.x - tragionR.x) * frameW;
  }
  return detection.boundingBox?.width ?? 0;
}

/**
 * `wasOk` is the previous frame's verdict. It widens the tooFar and offCentre
 * bands by `FRAMING.HYSTERESIS`, so a framing a cold start would reject as
 * borderline stays accepted — that IS the point: a subject sitting on a
 * threshold otherwise flips the box, and the shutter, as fast as the detector
 * runs.
 *
 * It reaches no other verdict. noFace, a missing anchor and tooClose are all
 * decided before the slack is consulted, so it can never conjure an accepted
 * square out of a frame with no usable face in it.
 */
export function evaluateFraming(
  detections: RawDetection[],
  frameW: number,
  frameH: number,
  wasOk = false,
): Framing {
  // A frame with no dimensions yields NaN everywhere below, and a NaN crop
  // becomes `left: NaN%` on the overlay. Callers guard on `videoWidth`, so
  // this is belt and braces — but it belongs here, with the rest of the maths.
  if (detections.length === 0 || frameW <= 0 || frameH <= 0) {
    return { ok: false, hint: 'noFace', crop: null };
  }

  // Largest head wins: in a queue, the person being photographed is the one
  // closest to the camera.
  const subject = detections.reduce((biggest, candidate) =>
    headWidthOf(candidate, frameW) > headWidthOf(biggest, frameW)
      ? candidate
      : biggest,
  );

  const anchors = anchorsOf(subject, frameW, frameH);
  if (!anchors || anchors.headWidthPx <= 0) {
    return { ok: false, hint: 'noFace', crop: null };
  }

  const sidePx = anchors.headWidthPx / FRAMING.HEAD_WIDTH_RATIO;
  const rawX = anchors.eyeCentreXPx - sidePx / 2;
  const rawY = anchors.eyeLineYPx - FRAMING.EYE_LINE_FROM_TOP * sidePx;

  // Too close is decided before clamping: a square that cannot fit has no
  // in-bounds position to be judged against.
  //
  // The square still TRACKS THE FACE. Centring it on the frame instead — what
  // this used to do — freezes it the moment the subject leans in, and a frozen
  // box reads as a broken one: the operator sees a rectangle that ignores them
  // while the hint talks about distance. Same anchor rule as below, with the
  // side capped at what the frame can hold. On the axis the square fills there
  // is no room left, so the clamp pins it — that part is geometry, not a
  // choice.
  if (sidePx > frameW || sidePx > frameH) {
    const fitted = Math.min(frameW, frameH);
    const fittedX = anchors.eyeCentreXPx - fitted / 2;
    const fittedY = anchors.eyeLineYPx - FRAMING.EYE_LINE_FROM_TOP * fitted;
    return {
      ok: false,
      hint: 'tooClose',
      crop: {
        x: Math.min(Math.max(fittedX, 0), frameW - fitted) / frameW,
        y: Math.min(Math.max(fittedY, 0), frameH - fitted) / frameH,
        size: fitted / frameW,
      },
    };
  }

  const x = Math.min(Math.max(rawX, 0), frameW - sidePx);
  const y = Math.min(Math.max(rawY, 0), frameH - sidePx);
  const crop: NormalisedRect = {
    x: x / frameW,
    y: y / frameH,
    size: sidePx / frameW,
  };

  const slack = wasOk ? FRAMING.HYSTERESIS : 1;

  if (anchors.headWidthPx / frameW < FRAMING.MIN_HEAD_WIDTH / slack) {
    return { ok: false, hint: 'tooFar', crop };
  }

  const clampLoss = Math.max(Math.abs(x - rawX), Math.abs(y - rawY)) / sidePx;
  if (clampLoss > FRAMING.MAX_CLAMP_LOSS * slack) {
    return { ok: false, hint: 'offCentre', crop };
  }

  if (detections.length > 1) {
    return { ok: false, hint: 'multipleFaces', crop };
  }

  return { ok: true, hint: null, crop };
}

/** `side` scales both knobs: a distant subject's square may wobble by fewer
 *  pixels than a close one's and still be the same visible shimmer. */
function ease(from: number, to: number, side: number): number {
  if (Math.abs(to - from) < FRAMING.DEADBAND * side) {
    return from;
  }
  return from * FRAMING.SMOOTHING + to * (1 - FRAMING.SMOOTHING);
}

/**
 * Eases the new square towards the previous one, so the overlay stops
 * shimmering on a still subject. Applied to the verdict as a whole rather than
 * to the raw detection: the hint is what the operator reads, and it must
 * describe the square actually drawn.
 *
 * x is normalised by width and y by height, so measuring one jump against a
 * single scale is approximate — deliberately, since it only decides
 * ease-versus-snap.
 */
export function stabilise(prev: Framing, next: Framing): Framing {
  const from = prev.crop;
  const to = next.crop;
  if (!from || !to) {
    return next;
  }
  const jump = Math.max(
    Math.abs(from.x - to.x),
    Math.abs(from.y - to.y),
    Math.abs(from.size - to.size),
  );
  if (jump > FRAMING.SNAP_DISTANCE * to.size) {
    return next;
  }
  return {
    ...next,
    crop: {
      x: ease(from.x, to.x, to.size),
      y: ease(from.y, to.y, to.size),
      size: ease(from.size, to.size, to.size),
    },
  };
}

/**
 * The square to pre-fill the crop dialog with — none, for a shot the gate
 * rejected.
 *
 * An escape-hatch shot is taken *because* the framing was refused: off-centre,
 * too close, several faces. Pre-selecting that square would hand the operator
 * back the very framing they just overrode, so the dialog opens on its default
 * centred square instead.
 *
 * Worth a named function rather than an inline ternary because a refused framing
 * still *carries* a crop: `evaluateFraming` computes the square before deciding
 * whether it is acceptable, and every rejection above returns it. Reading
 * `framing.crop` without consulting `framing.ok` therefore yields a plausible
 * rectangle rather than an empty one — a mistake nothing would report.
 */
export function cropToPreFill(framing: Framing): NormalisedRect | null {
  return framing.ok ? framing.crop : null;
}

/**
 * Converts a normalised square to react-image-crop's pixel Crop, against the
 * displayed size of the image. The side is taken from the width on both axes
 * so the result stays square in pixels.
 */
export function toPixelCrop(
  rect: NormalisedRect,
  displayW: number,
  displayH: number,
): { unit: 'px'; x: number; y: number; width: number; height: number } {
  const side = rect.size * displayW;
  return {
    unit: 'px',
    x: rect.x * displayW,
    y: rect.y * displayH,
    width: side,
    height: side,
  };
}
